import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AccountBalance,
  CheckCircle,
  Warning,
  Edit,
  Visibility,
  FilterList,
  Download,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface ProviderBanking {
  id: string;
  provider: {
    id: string;
    name: string;
    businessName: string;
    email: string;
  };
  bankName: string;
  accountType: 'checking' | 'savings';
  routingNumber: string;
  accountNumber: string;
  accountHolderName: string;
  isVerified: boolean;
  verificationDate?: string;
  lastUsed?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminBankingManagement: React.FC = () => {
  const [bankingData, setBankingData] = useState<ProviderBanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'verified' | 'unverified' | 'active' | 'inactive'>('all');
  const [selectedBanking, setSelectedBanking] = useState<ProviderBanking | null>(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBankingData();
  }, [filter]);

  const fetchBankingData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filter === 'verified') params.append('verified', 'true');
      if (filter === 'unverified') params.append('verified', 'false');
      if (filter === 'active') params.append('active', 'true');
      if (filter === 'inactive') params.append('active', 'false');

      const response = await fetch(`${API_URL}/provider-banking/admin/all?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBankingData(data.data ?? []);
          setError(null);
        } else {
          setError(data.error || 'Failed to load banking data');
        }
      } else {
        const errBody = await response.json().catch(() => ({}));
        setError(errBody?.error || `Failed to load banking data (${response.status})`);
      }
    } catch (error) {
      console.error('Error fetching banking data:', error);
      setError('Failed to load banking data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (bankingId: string, verified: boolean) => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/provider-banking/admin/${bankingId}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ verified }),
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setBankingData(prev => 
          prev.map(banking => 
            banking.id === bankingId 
              ? { 
                  ...banking, 
                  isVerified: verified,
                  verificationDate: verified ? new Date().toISOString() : undefined
                }
              : banking
          )
        );
        setVerifyDialog(false);
        setSelectedBanking(null);
      } else {
        setError(data.error || 'Failed to update verification status');
      }
    } catch (error) {
      console.error('Error updating verification:', error);
      setError('Failed to update verification status');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const csvData = bankingData.map(banking => ({
      'Provider Name': banking.provider.name,
      'Business Name': banking.provider.businessName,
      'Email': banking.provider.email,
      'Bank Name': banking.bankName,
      'Account Type': banking.accountType,
      'Routing Number': banking.routingNumber,
      'Account Number': banking.accountNumber,
      'Account Holder': banking.accountHolderName,
      'Verified': banking.isVerified ? 'Yes' : 'No',
      'Verification Date': banking.verificationDate ? new Date(banking.verificationDate).toLocaleDateString() : '',
      'Last Used': banking.lastUsed ? new Date(banking.lastUsed).toLocaleDateString() : '',
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `provider-banking-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredData = bankingData.filter(banking => {
    switch (filter) {
      case 'verified':
        return banking.isVerified;
      case 'unverified':
        return !banking.isVerified;
      case 'active':
        return true; // All active by default
      case 'inactive':
        return false; // No inactive records in current implementation
      default:
        return true;
    }
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          <AccountBalance sx={{ mr: 1, verticalAlign: 'middle' }} />
          Provider Banking Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<Download />}
            onClick={handleExport}
            variant="outlined"
            disabled={bankingData.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }} icon={<AccountBalance />}>
        Provider payouts are processed via <strong>Stripe Connect</strong> (see Weekly Payouts). This table shows optional manual bank account details (routing/account) only if providers have added them.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  label="Filter"
                  startAdornment={<FilterList sx={{ mr: 1 }} />}
                >
                  <MenuItem value="all">All Providers</MenuItem>
                  <MenuItem value="verified">Verified Only</MenuItem>
                  <MenuItem value="unverified">Unverified Only</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredData.length} of {bankingData.length} providers
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Bank</TableCell>
              <TableCell>Account Type</TableCell>
              <TableCell>Account Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((banking) => (
              <TableRow key={banking.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {banking.provider.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {banking.provider.businessName}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {banking.provider.email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {banking.bankName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {banking.accountHolderName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={banking.accountType}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {banking.accountNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Routing: {banking.routingNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={banking.isVerified ? <CheckCircle /> : <Warning />}
                    label={banking.isVerified ? 'Verified' : 'Pending'}
                    color={banking.isVerified ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedBanking(banking);
                          setDetailDialog(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={banking.isVerified ? 'Mark as Unverified' : 'Mark as Verified'}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedBanking(banking);
                          setVerifyDialog(true);
                        }}
                        color={banking.isVerified ? 'warning' : 'success'}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No banking information found for the selected filter.
          </Typography>
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Banking Details</DialogTitle>
        <DialogContent>
          {selectedBanking && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Provider</Typography>
                <Typography variant="body1">{selectedBanking.provider.name}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedBanking.provider.businessName}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedBanking.provider.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Bank</Typography>
                <Typography variant="body1">{selectedBanking.bankName}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedBanking.accountHolderName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Account Type</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{selectedBanking.accountType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Routing Number</Typography>
                <Typography variant="body1" fontFamily="monospace">{selectedBanking.routingNumber}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Account Number</Typography>
                <Typography variant="body1" fontFamily="monospace">{selectedBanking.accountNumber}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  icon={selectedBanking.isVerified ? <CheckCircle /> : <Warning />}
                  label={selectedBanking.isVerified ? 'Verified' : 'Pending'}
                  color={selectedBanking.isVerified ? 'success' : 'warning'}
                  size="small"
                />
              </Grid>
              {selectedBanking.verificationDate && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Verified On</Typography>
                  <Typography variant="body1">
                    {new Date(selectedBanking.verificationDate).toLocaleString()}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialog} onClose={() => setVerifyDialog(false)}>
        <DialogTitle>
          {selectedBanking?.isVerified ? 'Mark as Unverified' : 'Mark as Verified'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {selectedBanking?.isVerified ? 'unverify' : 'verify'} the banking information for{' '}
            <strong>{selectedBanking?.provider.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)}>Cancel</Button>
          <Button
            onClick={() => selectedBanking && handleVerify(selectedBanking.id, !selectedBanking.isVerified)}
            color={selectedBanking?.isVerified ? 'warning' : 'success'}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : undefined}
          >
            {saving ? 'Updating...' : (selectedBanking?.isVerified ? 'Unverify' : 'Verify')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminBankingManagement;
